import { useState, type MouseEvent } from 'react';
import { Mail, MessageCircle } from 'lucide-react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import type { LeadTemplate, ResendChannel } from '@/api/resendInvite';

interface ResendInviteButtonsProps {
  hasEmail: boolean;
  hasPhone: boolean;
  onResend?: (channel: ResendChannel) => Promise<void>;
  onResendWithTemplate?: (channel: ResendChannel, templateId: string) => Promise<void>;
  loadTemplates?: () => Promise<LeadTemplate[]>;
  size?: 'sm' | 'md';
}

export default function ResendInviteButtons({
  hasEmail,
  hasPhone,
  onResend,
  onResendWithTemplate,
  loadTemplates,
  size = 'sm',
}: ResendInviteButtonsProps) {
  const [loadingChannel, setLoadingChannel] = useState<ResendChannel | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<ResendChannel | null>(null);
  const [templates, setTemplates] = useState<LeadTemplate[]>([]);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const disabled = loadingChannel !== null;
  const buttonSize = size === 'md' ? 'md' : 'sm';
  const buttonClass = size === 'md' ? '!w-10 !px-0' : '!w-9 !px-0';
  const iconClass = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  const handleResend = async (event: MouseEvent<HTMLButtonElement>, channel: ResendChannel) => {
    event.stopPropagation();

    if (onResendWithTemplate && loadTemplates) {
      setSelectedChannel(channel);
      setTemplateError(null);
      setLoadingTemplates(true);
      try {
        setTemplates(await loadTemplates());
      } catch (error) {
        setTemplateError(error instanceof Error ? error.message : 'Failed to load templates');
      } finally {
        setLoadingTemplates(false);
      }
      return;
    }

    if (!onResend) return;
    setLoadingChannel(channel);
    try {
      await onResend(channel);
    } finally {
      setLoadingChannel(null);
    }
  };

  const handleTemplateSend = async (templateId: string) => {
    if (!selectedChannel || !onResendWithTemplate) return;
    setLoadingChannel(selectedChannel);
    try {
      await onResendWithTemplate(selectedChannel, templateId);
      setSelectedChannel(null);
    } finally {
      setLoadingChannel(null);
    }
  };

  const filteredTemplates = selectedChannel
    ? templates.filter((template) => template.channel === selectedChannel || template.channel === 'both')
    : [];

  return (
    <>
      <div className="inline-flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size={buttonSize}
          className={buttonClass}
          disabled={disabled || !hasEmail}
          loading={loadingChannel === 'email'}
          title={hasEmail ? 'Resend invite by email' : 'No email on file'}
          onClick={(event) => void handleResend(event, 'email')}
        >
          {loadingChannel !== 'email' && <Mail className={iconClass} />}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size={buttonSize}
          className={buttonClass}
          disabled={disabled || !hasPhone}
          loading={loadingChannel === 'whatsapp'}
          title={hasPhone ? 'Resend invite by WhatsApp' : 'No phone on file'}
          onClick={(event) => void handleResend(event, 'whatsapp')}
        >
          {loadingChannel !== 'whatsapp' && <MessageCircle className={iconClass} />}
        </Button>
      </div>

      <Modal
        isOpen={selectedChannel !== null}
        onClose={() => setSelectedChannel(null)}
        title={`Pick a template (${selectedChannel === 'email' ? 'Email' : 'WhatsApp'})`}
        size="md"
      >
        {loadingTemplates ? (
          <div className="py-8 text-center text-sm text-ink-secondary">Loading templates...</div>
        ) : templateError ? (
          <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
            {templateError}
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="py-8 text-center text-sm text-ink-secondary">No templates available</div>
        ) : (
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-navy-border p-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink-primary">{template.name}</div>
                  <div className="mt-1 line-clamp-2 text-xs text-ink-secondary">{template.preview}</div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  loading={loadingChannel === selectedChannel}
                  onClick={() => void handleTemplateSend(template.id)}
                >
                  Send
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
